import { FLAG_FULL_ROLLOUT_BP } from 'definitions';
// The evaluator's own rule, so the figure is what the SDK and the runtime serve. Pure.
import { rolloutLimit } from 'flag-evaluator/rollout';

type RolloutFigureParams = {
  /** The rollout as saved. */
  savedBp: number;
  /** The rollout as typed or slid, null while nothing is (or what is typed is not a share). */
  draftBp: number | null;
  holdbackBp: number;
  isArchived: boolean;
};

export type RolloutFigure = {
  /** The share asked for: the draft while there is one, else what is saved. */
  requestedBp: number;
  /** The share that gets On at that rollout: min(rollout, 100% minus the holdback). */
  onBp: number;
  /** The share that gets On now, from what is saved. */
  liveOnBp: number;
  /** Whether the figure shows a share that is not saved yet. */
  isUnsaved: boolean;
  /** Whether the holdback holds the figure below the share asked for. */
  isCapped: boolean;
  /** The most that can get On with this holdback. */
  maxOnBp: number;
};

/**
 * The Rollout card's big figure. It follows the slider and the field while they are being
 * changed, and shows the share that actually gets On: the holdback takes the top of the bucket
 * range (flag-evaluator rolloutLimit), so a 100% rollout with a 10% holdback reaches 90%.
 * An archived flag serves Off to everyone.
 */
export function rolloutFigure({ savedBp, draftBp, holdbackBp, isArchived }: RolloutFigureParams): RolloutFigure {
  const maxOnBp = rolloutLimit(FLAG_FULL_ROLLOUT_BP, holdbackBp);
  if (isArchived) {
    return { requestedBp: 0, onBp: 0, liveOnBp: 0, isUnsaved: false, isCapped: false, maxOnBp };
  }
  const requestedBp = draftBp ?? savedBp;
  const isUnsaved = requestedBp !== savedBp;
  const onBp = rolloutLimit(requestedBp, holdbackBp);
  return {
    requestedBp,
    onBp,
    liveOnBp: rolloutLimit(savedBp, holdbackBp),
    isUnsaved,
    isCapped: onBp < requestedBp,
    maxOnBp,
  };
}
